import axios from 'axios';
import { supabase } from '../lib/supabase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

console.log('API Base URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000,
});

let _authToken = null;
let _pendingRequests = {};

export const setAuthToken = (token) => {
  _authToken = token;
};

export const dedupedGet = (url) => {
  if (_pendingRequests[url]) {
    return _pendingRequests[url];
  }
  const promise = api.get(url);
  _pendingRequests[url] = promise;
  promise.finally(() => {
    delete _pendingRequests[url];
  });
  return promise;
};

// Synchronous interceptor to bypass session hang
api.interceptors.request.use((config) => {
  if (config.headers.Authorization && config.headers.Authorization.toString().startsWith('Bearer ')) {
    console.log('Using pre-set token for request:', config.url);
    return config;
  }

  if (_authToken) {
    config.headers.Authorization = `Bearer ${_authToken}`;
    console.log('Token attached from cache:', config.url);
  } else {
    console.log('No token available for request:', config.url);
  }

  return config;
});


// Handle response errors with comprehensive error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if error has a response (not a network error)
    if (!error.response) {
      // Network error - no response from server
      error.message = 'Connection failed. Please check your internet and try again.';
      return Promise.reject(error);
    }

    const status = error.response.status;

    // 401 Unauthorized - Clear token and redirect to login
    if (status === 401) {
      console.warn('401 Unauthorized:', error.config?.url);
      _authToken = null;
      
      // Only redirect if not already on login/signup page
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/signup') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // 429 Too Many Requests - Rate limit
    if (status === 429) {
      error.message = 'Too many requests. Please wait a moment and try again.';
      return Promise.reject(error);
    }

    // 403 Forbidden - Permission denied
    if (status === 403) {
      error.message = 'You don\'t have permission to perform this action.';
      return Promise.reject(error);
    }

    // 500 Server Error
    if (status === 500) {
      error.message = 'Server error. Please try again later.';
      return Promise.reject(error);
    }

    // For other errors, use detail from response if available
    if (error.response.data?.detail) {
      error.message = error.response.data.detail;
    }

    return Promise.reject(error);
  }
);

// ============ AUTH ENDPOINTS ============
export const getCurrentUser = async () => {
  const res = await dedupedGet('/auth/me');
  return res.data;
};

// Alias for backward compatibility
export const getMe = getCurrentUser;

// ============ PLAN ENDPOINTS ============
export const getUserPlan = async () => {
  const res = await api.get('/user/plan');
  return res.data;
};

export const upgradePlan = async (plan) => {
  const res = await api.post('/user/upgrade', { plan });
  return res.data;
};

export const downgradePlan = async () => {
  const res = await api.post('/user/downgrade');
  return res.data;
};

// ============ SEARCH ENDPOINTS ============
export const startSearch = async (params) => {
  const res = await api.post('/search', params);
  return res.data;
};

export const getJobStatus = async (jobId) => {
  const res = await api.get(`/status/${jobId}`);
  return res.data;
};

export const getJobResults = async (jobId, skip = 0, limit = 1000) => {
  const res = await api.get(`/results/${jobId}`, { params: { skip, limit } });
  return res.data;
};

// Aliases for backward compatibility
export const getResults = getJobResults;
export const checkJobStatus = getJobStatus;
export const pollJobStatus = getJobStatus;

export const triggerDownload = (blobData, filename) => {
  const blob = new Blob([blobData]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ============ JOBS/HISTORY ENDPOINTS ============
export const getUserJobs = async () => {
  const res = await api.get('/jobs');
  return res.data;
};

// Alias
export const listJobs = getUserJobs;

export const deleteJob = async (jobId) => {
  const res = await api.delete(`/jobs/${jobId}`);
  return res.data;
};

// ============ EMAIL ENDPOINTS ============
export const findEmail = async (placeId, website) => {
  const res = await api.post('/leads/find-email', { place_id: placeId, website });
  return res.data;
};

export const bulkFindEmail = async (leads) => {
  const res = await api.post('/leads/bulk-find-email', { leads });
  return res.data;
};

// ============ DOWNLOAD ENDPOINTS ============
export const downloadResults = async (jobId, format = 'csv') => {
  const res = await api.get(`/download/${jobId}`, {
    params: { format },
    responseType: 'blob',
  });
  return res.data;
};

// ============ COUNTRIES ENDPOINTS ============
export const getCountries = async () => {
  const res = await api.get('/countries');
  return res.data;
};

export const getCities = async (countryId) => {
  const res = await api.get(`/countries/${countryId}/cities`);
  return res.data;
};

// ============ CONTACT ENDPOINT ============
export const submitContact = async (name, email, message) => {
  const res = await api.post('/contact', { name, email, message });
  return res.data;
};

// ============ PAYMENT ENDPOINTS ============
export const createPaymentOrder = async (plan) => {
  const res = await api.post('/payment/create-order', { plan });
  return res.data;
};

export const verifyPayment = async (paymentData) => {
  const res = await api.post('/payment/verify', paymentData);
  return res.data;
};

// ============ FILTER ENDPOINT ============
export const filterResults = async (jobId, filters) => {
  const res = await api.post(`/results/${jobId}/filter`, filters);
  return res.data;
};

// ============ ADMIN ENDPOINTS ============
export const adminGetUsers = async () => {
  const res = await api.get('/admin/users');
  return res.data;
};

export const adminGetStats = async () => {
  const res = await api.get('/admin/stats');
  return res.data;
};

export const adminBlockUser = async (userId, block) => {
  const res = await api.post(`/admin/block-user`, { user_id: userId, block: block });
  return res.data;
};

export const adminChangePlan = async (userId, plan) => {
  const res = await api.post(`/admin/change-plan`, { user_id: userId, plan: plan });
  return res.data;
};

export const adminGetPayments = async () => {
  const res = await api.get('/admin/payments');
  return res.data;
};

export const adminGetContacts = async () => {
  const res = await api.get('/admin/contacts');
  return res.data;
};

// ============ AI ENDPOINTS ============
export const aiParsePrompt = async (prompt) => {
  const res = await api.post('/ai/parse', { prompt });
  return res.data;
};

export const aiChatSearch = async (prompt, parsedData = null, conversationHistory = null) => {
  const res = await api.post('/ai/search', { prompt, parsed_data: parsedData, conversation_history: conversationHistory });
  return res.data;
};

// ============ CHAT SESSION PERSISTENCE ============

export const saveChatSession = async (sessionData) => {
  const res = await api.post('/chat/save', sessionData);
  return res.data;
};

export const getChatHistory = async () => {
  const res = await api.get('/chat/history');
  return res.data;
};

export const getLatestChat = async () => {
  const res = await api.get('/chat/latest');
  return res.data;
};

export const getChatSession = async (sessionId) => {
  const res = await api.get(`/chat/${sessionId}`);
  return res.data;
};

export const deleteChatSession = async (sessionId) => {
  const res = await api.delete(`/chat/${sessionId}`);
  return res.data;
};

// ============ YOUTUBE ENDPOINTS ============

export const youtubeSearch = async (query, freelancerType = null, maxResults = 20, minSubs = 0, maxSubs = 0, country = null, conversationHistory = null) => {
  const res = await api.post('/youtube/search', {
    query,
    freelancer_type: freelancerType,
    max_results: maxResults || 20,
    min_subs: minSubs || 0,
    max_subs: maxSubs || 0,
    country: country || null,
    conversation_history: conversationHistory || []
  });
  return res.data;
};

export const getYoutubeQuota = async () => {
  const res = await api.get('/youtube/quota');
  return res.data;
};

// Freelancer APIs
export const getFreelancerTypes = async () => {
  const res = await api.get('/freelancer/types');
  return res.data;
};

export const getFreelancerProfile = async () => {
  const res = await api.get('/freelancer/profile');
  return res.data;
};

export const saveFreelancerProfile = async (profileData) => {
  const res = await api.post('/freelancer/profile', profileData);
  return res.data;
};

export const deleteFreelancerProfile = async () => {
  const res = await api.delete('/freelancer/profile');
  return res.data;
};

export default api;
